import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { fmtINR, waLink } from "../../lib/constants";
import { Search, MessageCircle, Plus, X } from "lucide-react";
import { toast } from "sonner";
import OtpVerify from "../../components/OtpVerify";
import DateInput from "../../components/DateInput";
import { useAuth } from "../../lib/auth";

export default function CustomersAdmin() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null);
  const [view360, setView360] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [needsOtp, setNeedsOtp] = useState(false);
  const [otpDone, setOtpDone] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", birthday: "", anniversary: "", location: "", notes: "", address: "", alt_phone: "", id_type: "Aadhaar", id_number: "" });
  const [phoneError, setPhoneError] = useState("");
  const [editCust, setEditCust] = useState(null); // customer being edited (super admin)
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");

  const saveEdit = async (e) => {
    e.preventDefault();
    if ((editCust.id_type || "").toLowerCase() === "aadhaar" && (editCust.id_number || "").trim() && !/^\d{12}$/.test((editCust.id_number || "").replace(/\s/g, ""))) {
      toast.error("Aadhaar number must be exactly 12 digits"); return;
    }
    try {
      const body = {
        name: editCust.name, email: editCust.email,
        birthday: editCust.birthday, anniversary: editCust.anniversary,
        location: editCust.location, notes: editCust.notes,
        address: editCust.address, alt_phone: editCust.alt_phone,
        id_type: editCust.id_type, id_number: editCust.id_number,
      };
      if (isSuper) {
        // Super admin can also change the phone (the record key).
        await api.patch(`/api/crm/customers/${editCust.id}`, { ...body, phone: editCust.phone });
      } else {
        // Salesperson / admin / major: fill or correct details via enrich.
        await api.patch(`/api/crm/customers/${editCust.id}/enrich`, body);
      }
      toast.success("Customer updated");
      setEditCust(null); setView360(null); setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteCust = async (c) => {
    if (!window.confirm(`Permanently delete customer ${c.name} (${c.phone})? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/crm/customers/${c.id}`);
      toast.success("Customer deleted");
      setView360(null); setActive(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const load = () => api.get(`/api/crm/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`).then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const open360 = async (phone) => {
    setActive(phone);
    const { data } = await api.get(`/api/crm/customers/by-phone/${encodeURIComponent(phone)}`);
    setView360(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      setPhoneError("Enter a valid 10-digit mobile number starting with 6-9.");
      return;
    }
    setPhoneError("");
    if (!form.address.trim()) { toast.error("Address is required for a new customer"); return; }
    if (!form.birthday) { toast.error("Date of birth is required for a new customer"); return; }
    if ((form.id_type || "").toLowerCase() === "aadhaar" && (form.id_number || "").trim() && !/^\d{12}$/.test(form.id_number.replace(/\s/g, ""))) {
      toast.error("Aadhaar number must be exactly 12 digits"); return;
    }
    try {
      await api.post("/api/crm/customers", form);
      toast.success("Customer added");
      setShowForm(false);
      setOtpDone(false);
      setForm({ name: "", phone: "", email: "", birthday: "", anniversary: "", location: "", notes: "", address: "", alt_phone: "", id_type: "Aadhaar", id_number: "" });
      load();
    } catch (e) {
      // 428 = backend says this is a NEW customer whose number isn't verified yet
      if (e?.response?.status === 428) {
        setNeedsOtp(true);
        toast.error("New customer — verify their mobile number with an OTP first");
      } else if (e?.response?.status === 409) {
        // Phone already belongs to an existing customer
        toast.error(e?.response?.data?.detail || "This phone number already exists");
      } else {
        toast.error(e?.response?.data?.detail || "Failed");
      }
    }
  };

  return (
    <div className="p-6 lg:p-8 grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <h1 className="font-display text-3xl text-maroon">Customers</h1>
          <button onClick={() => setShowForm(true)} className="btn-outline text-xs" data-testid="add-customer-btn"><Plus size={12}/></button>
        </div>
        <div className="relative mb-4">
          <Search size={14} className="absolute left-3 top-3 text-ash"/>
          <input data-testid="customer-search" placeholder="Search name / phone / email" value={q} onChange={(e) => setQ(e.target.value)} className="w-full border border-line pl-9 pr-3 py-2 bg-white text-sm"/>
        </div>
        <div className="bg-white border border-line max-h-[70vh] overflow-y-auto">
          {items.map((c) => (
            <button key={c.id} onClick={() => open360(c.phone)} className={`w-full text-left p-3 border-b border-line/60 hover:bg-pearl ${active === c.phone ? "bg-pearl" : ""}`} data-testid={`customer-${c.phone}`}>
              <div className="font-medium">{c.name}</div>
              <div className="text-xs text-ash">{c.phone} · LTV {fmtINR(c.ltv || 0)}</div>
              <div className="text-[10px] mt-0.5">
                {c.handled_by_name
                  ? <span className="text-maroon">Handled by {c.handled_by_name}</span>
                  : <span className="text-ash italic">Unassigned</span>}
              </div>
            </button>
          ))}
          {items.length === 0 && <div className="p-6 text-center text-xs text-ash">No customers yet.</div>}
        </div>
      </div>

      <div className="lg:col-span-2">
        {!view360 ? (
          <div className="bg-white border border-line p-12 text-center text-ash">Select a customer to view 360° profile.</div>
        ) : view360.customer ? (
          <div>
            <div className="bg-maroon text-pearl p-5">
              <div className="eyebrow text-gold-light">Customer 360°</div>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="font-display text-2xl sm:text-3xl mt-1 break-words min-w-0">{view360.customer.name}</div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => setEditCust({ ...view360.customer })} className="bg-gold text-ink text-xs px-3 py-1.5" data-testid="edit-customer-btn">Edit</button>
                  {isSuper && (
                    <button onClick={() => deleteCust(view360.customer)} className="bg-red-700 text-pearl text-xs px-3 py-1.5" data-testid="delete-customer-btn">Delete</button>
                  )}
                </div>
              </div>
              <div className="text-sm text-pearl/80 mt-1">{view360.customer.phone} {view360.customer.email && `· ${view360.customer.email}`}</div>
              <div className="grid grid-cols-3 gap-3 mt-4 text-center">
                <div><div className="text-[10px] text-pearl/60">LTV</div><div className="font-display text-2xl">{fmtINR(view360.customer.ltv || 0)}</div></div>
                <div><div className="text-[10px] text-pearl/60">Orders</div><div className="font-display text-2xl">{view360.customer.orders_count || 0}</div></div>
                <div><div className="text-[10px] text-pearl/60">Loyalty pts</div><div className="font-display text-2xl">{view360.customer.loyalty_points || 0}</div></div>
              </div>
              <a href={waLink(`Hello ${view360.customer.name}, this is Swayambhu Gold.`)} target="_blank" rel="noreferrer" className="btn-gold mt-4 inline-flex" data-testid="customer-wa-btn">
                <MessageCircle size={14}/> WhatsApp Customer
              </a>
            </div>

            {/* Full customer record — birthday, anniversary, photo ID, address, contact */}
            <div className="bg-white border border-line border-t-0 p-5" data-testid="customer-details">
              <div className="eyebrow text-[10px] mb-3">Customer Details</div>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                <DetailRow label="Date of Birth" value={view360.customer.birthday ? new Date(view360.customer.birthday).toLocaleDateString("en-GB") : ""}/>
                <DetailRow label="Anniversary" value={view360.customer.anniversary ? new Date(view360.customer.anniversary).toLocaleDateString("en-GB") : ""}/>
                <DetailRow label="Photo ID" value={[view360.customer.id_type, view360.customer.id_number].filter(Boolean).join(" · ")}/>
                <DetailRow label="Email" value={view360.customer.email}/>
                <DetailRow label="Other Number" value={view360.customer.alt_phone}/>
                <DetailRow label="Address" value={view360.customer.address || view360.customer.location}/>
                {view360.customer.referral_code && <DetailRow label="Referral Code" value={view360.customer.referral_code}/>}
                {(view360.customer.tags || []).length > 0 && <DetailRow label="Tags" value={(view360.customer.tags || []).join(", ")}/>}
                <DetailRow label="Handled by" value={view360.customer.handled_by_name}/>
              </div>
              {view360.customer.notes && (
                <div className="mt-3 text-xs text-ash"><b className="text-maroon">Notes:</b> {view360.customer.notes}</div>
              )}
            </div>

            {view360.store_credit && (view360.store_credit.credits?.length > 0 || view360.store_credit.balance > 0) && (
              <StoreCreditPanel data={view360.store_credit} />
            )}

            <Tabs key={view360.customer.phone} sections={{
              "Orders": view360.orders,
              "Schemes": view360.schemes,
              "Gold Scheme": view360.gold_schemes,
              "Karigar Jobs": view360.karigar_jobs,
              "Follow-ups": view360.followups,
            }} />
          </div>
        ) : (
          <div className="bg-white border border-line p-12 text-center text-ash">No customer record found.</div>
        )}
      </div>

      {/* Super Admin: edit customer — correctly scoped inside CustomersAdmin
          this time, where editCust/setEditCust/saveEdit actually live. */}
      {editCust && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setEditCust(null)}>
          <form onSubmit={saveEdit} className="bg-white border border-line p-6 w-full max-w-md space-y-3 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">Edit customer</div>
              <button type="button" onClick={() => setEditCust(null)}><X size={18}/></button>
            </div>
            <input required placeholder="Name" className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.name || ""} onChange={(e) => setEditCust({...editCust, name: e.target.value})} data-testid="edit-cust-name"/>
            <input required placeholder="Phone (10 digits)" maxLength={10} disabled={!isSuper} className={`w-full border border-line px-3 py-2 bg-pearl ${!isSuper ? "opacity-60 cursor-not-allowed" : ""}`} value={editCust.phone || ""} onChange={(e) => setEditCust({...editCust, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="edit-cust-phone"/>
            {!isSuper && <p className="text-[10px] text-ash -mt-2">Only a super admin can change the phone number.</p>}
            <input placeholder="Email" className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.email || ""} onChange={(e) => setEditCust({...editCust, email: e.target.value.toLowerCase()})} data-testid="edit-cust-email"/>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Birthday</span>
              <DateInput className="mt-1" value={editCust.birthday || ""} onChange={(v) => setEditCust({...editCust, birthday: v})}/></label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Anniversary</span>
              <DateInput className="mt-1" value={editCust.anniversary || ""} onChange={(v) => setEditCust({...editCust, anniversary: v})}/></label>
            <textarea placeholder="Address" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.address || ""} onChange={(e) => setEditCust({...editCust, address: e.target.value})} data-testid="edit-cust-address"/>
            <input placeholder="Other number" maxLength={10} className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.alt_phone || ""} onChange={(e) => setEditCust({...editCust, alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="edit-cust-alt-phone"/>
            <div className="grid grid-cols-2 gap-2">
              <select className="border border-line px-3 py-2 bg-pearl" value={editCust.id_type || "Aadhaar"} onChange={(e) => setEditCust({...editCust, id_type: e.target.value})} data-testid="edit-cust-id-type">
                {["Aadhaar", "PAN", "Driving Licence", "Voter ID", "Passport"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <input placeholder="ID card number" className="border border-line px-3 py-2 bg-pearl" value={editCust.id_number || ""} onChange={(e) => setEditCust({...editCust, id_number: e.target.value})} data-testid="edit-cust-id-number"/>
            </div>
            <input placeholder="Location" className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.location || ""} onChange={(e) => setEditCust({...editCust, location: e.target.value})}/>
            <textarea placeholder="Notes" rows={2} className="w-full border border-line px-3 py-2 bg-pearl" value={editCust.notes || ""} onChange={(e) => setEditCust({...editCust, notes: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="save-cust-edit">Save Changes</button>
          </form>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-md space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between"><div className="font-display text-2xl text-maroon">Add customer</div><button type="button" onClick={() => setShowForm(false)}><X size={18}/></button></div>
            <input required placeholder="Name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="cust-name"/>
            <div>
              <input
                required
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="Phone (10 digits)"
                className={`w-full border px-3 py-2 bg-pearl ${phoneError ? "border-red-500" : "border-line"}`}
                value={form.phone}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setForm({...form, phone: v});
                  if (phoneError && /^[6-9]\d{9}$/.test(v)) setPhoneError("");
                }}
                data-testid="cust-phone"
              />
              {phoneError && <p className="text-xs text-red-600 mt-1" data-testid="cust-phone-error">{phoneError}</p>}
            </div>

            {/* New customers must have their number verified by OTP before saving */}
            {/^[6-9]\d{9}$/.test(form.phone) && (
              <div>
                {needsOtp && !otpDone && (
                  <p className="text-xs text-maroon mb-2" data-testid="new-customer-prompt">
                    This number isn't in our records — you're adding a <b>new customer</b>.
                    Verify it with an OTP, and fill in their address and ID below.
                  </p>
                )}
                <OtpVerify phone={form.phone} purpose="verify" onVerified={() => { setOtpDone(true); setNeedsOtp(false); }} />
              </div>
            )}

            <input placeholder="Email" className="w-full border border-line px-3 py-2 bg-pearl" value={form.email} onChange={(e) => setForm({...form, email: e.target.value.toLowerCase()})} data-testid="cust-email"/>

            {/* Compulsory address + other number + ID card */}
            <textarea
              required
              rows={2}
              placeholder="Address (required)"
              className="w-full border border-line px-3 py-2 bg-pearl"
              value={form.address}
              onChange={(e) => setForm({...form, address: e.target.value})}
              data-testid="cust-address"
            />
            <input
              type="tel"
              inputMode="numeric"
              maxLength={10}
              placeholder="Other number (optional)"
              className="w-full border border-line px-3 py-2 bg-pearl"
              value={form.alt_phone}
              onChange={(e) => setForm({...form, alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10)})}
              data-testid="cust-alt-phone"
            />
            <div className="grid grid-cols-2 gap-2">
              <select className="border border-line px-3 py-2 bg-pearl" value={form.id_type}
                onChange={(e) => setForm({...form, id_type: e.target.value})} data-testid="cust-id-type">
                {["Aadhaar", "PAN", "Driving Licence", "Voter ID", "Passport"].map((t) => <option key={t}>{t}</option>)}
              </select>
              <input placeholder="ID card number" className="border border-line px-3 py-2 bg-pearl"
                value={form.id_number} onChange={(e) => setForm({...form, id_number: e.target.value})} data-testid="cust-id-number"/>
            </div>
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Date of Birth (required)</span>
              <DateInput className="mt-1" value={form.birthday} onChange={(v) => setForm({...form, birthday: v})} data-testid="cust-birthday"/>
            </label>
            <label className="block text-sm">
              <span className="eyebrow text-[10px]">Anniversary (optional)</span>
              <DateInput className="mt-1" value={form.anniversary} onChange={(v) => setForm({...form, anniversary: v})} data-testid="cust-anniversary"/>
            </label>
            <textarea placeholder="Notes" rows={3} className="w-full border border-line px-3 py-2 bg-pearl" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            <button className="btn-maroon w-full justify-center" data-testid="create-customer-submit">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

/** A single label/value line in the customer detail card. Missing values are
 *  shown in maroon italics so gaps in the record are obvious. */
function DetailRow({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="text-ash whitespace-nowrap">{label}:</span>
      {value
        ? <span className="text-ink break-words">{value}</span>
        : <span className="text-maroon italic">not on file</span>}
    </div>
  );
}

function StoreCreditPanel({ data }) {
  const { balance = 0, credits = [], ledger = [] } = data || {};
  return (
    <div className="bg-white border border-gold rounded-lg overflow-hidden mb-4" data-testid="customer-store-credit">
      <div className="bg-gold/15 px-4 py-3 flex items-center justify-between">
        <div className="eyebrow text-maroon">Store Credit</div>
        <div className="font-display text-2xl text-maroon">{fmtINR(balance)} <span className="text-[11px] text-ash font-sans">available</span></div>
      </div>
      {/* Where each credit came from */}
      {credits.length > 0 && (
        <div className="px-4 py-3 border-b border-line">
          <div className="text-[10px] uppercase tracking-widest text-ash mb-2">Credits received</div>
          <div className="space-y-1.5">
            {credits.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 text-sm" data-testid={`credit-src-${c.id}`}>
                <div className="min-w-0">
                  <span className="font-medium text-maroon">{fmtINR(c.amount)}</span>
                  <span className="text-xs text-ash"> · {c.reason || "return"}{c.label_no ? ` · ${c.label_no}` : ""}{c.credit_mode === "today_rate" ? " · today's rate" : ""}</span>
                  <div className="text-[10px] text-ash">{c.raised_at ? new Date(c.raised_at).toLocaleDateString() : ""} · raised by {c.raised_by || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs text-ash">remaining {fmtINR(c.remaining)}</span>
                  <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${c.status === "approved" ? "bg-green-100 text-green-700" : c.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>{c.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Full earn / redeem history */}
      {ledger.length > 0 && (
        <div className="px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-ash mb-2">Usage history</div>
          <div className="space-y-1">
            {ledger.map((l) => (
              <div key={l.id} className="flex items-center justify-between text-xs" data-testid={`credit-ledger-${l.id}`}>
                <div>
                  <span className={l.kind === "earn" ? "text-green-700" : "text-maroon"}>{l.kind === "earn" ? "Received" : "Redeemed"}</span>
                  <span className="text-ash"> {l.kind === "earn" ? `from ${l.reason || "return"}` : `against ${l.ref_kind || "bill"} ${l.ref || ""}`}{l.claim_type ? ` (${l.claim_type})` : ""}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${l.kind === "earn" ? "text-green-700" : "text-maroon"}`}>{l.kind === "earn" ? "+" : "−"}{fmtINR(l.amount)}</span>
                  <span className="text-[10px] text-ash">{l.at ? new Date(l.at).toLocaleDateString() : ""}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Tabs({ sections }) {
  const keys = Object.keys(sections);
  const [active, setActive] = useState(keys[0]);
  return (
    <div className="mt-6 bg-white border border-line">
      <div className="flex border-b border-line">
        {keys.map((k) => (
          <button key={k} onClick={() => setActive(k)} className={`px-4 py-2 text-sm ${active === k ? "border-b-2 border-maroon text-maroon" : "text-ash"}`} data-testid={`tab-${k}`}>
            {k} <span className="ml-1 text-[10px] text-ash">({sections[k]?.length || 0})</span>
          </button>
        ))}
      </div>
      <div className="p-4 max-h-[50vh] overflow-y-auto">
        {(sections[active] || []).length === 0 ? (
          <div className="text-center py-8 text-xs text-ash">Nothing here yet.</div>
        ) : (
          <div className="space-y-2">
            {sections[active].map((it, i) => (
              <div key={it.id || i} className="bg-pearl border border-line p-3 text-sm">
                <div className="font-medium">{it.scheme_name || it.customer_name || it.reason || `#${(it.id || "").slice(0,8)}`}</div>
                <div className="text-xs text-ash mt-1">
                  {it.status && <>Status: <span className="text-maroon">{it.status}</span> · </>}
                  {it.total && <>Total: ₹{it.total} · </>}
                  {it.installments_paid !== undefined && <>{it.installments_paid}/{it.duration_months} paid · </>}
                  {it.total_paid !== undefined && <>Paid: {fmtINR(it.total_paid || 0)} · </>}
                  {it.total_grams !== undefined && <>{(it.total_grams || 0).toFixed(3)} g {it.purity || ""} · </>}
                  {it.created_at && new Date(it.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
